import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Project, FundRequest } from '../../types';
import apiService from '../../services/api';
import { 
  Card, 
  CardHeader, 
  CardBody, 
  CardFooter,
  Button, 
  Badge,
  Grid,
  FlexContainer
} from '../../styles/StyledComponents';
import IconWrapper from '../common/IconWrapper';
import { 
  FaProjectDiagram, 
  FaMoneyBillWave, 
  FaClock, 
  FaCheckCircle, 
  FaExclamationTriangle,
  FaEye,
  FaPlus,
  FaChartLine,
  FaCalculator,
  FaFileInvoiceDollar,
  FaArrowUp,
  FaArrowDown
} from 'react-icons/fa';

const DashboardContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const DashboardHeader = styled.div`
  margin-bottom: 2rem;
  
  h1 {
    color: var(--secondary-color);
    margin-bottom: 0.5rem;
    font-size: 2rem;
    
    @media (max-width: 768px) {
      font-size: 1.5rem;
    }
    
    @media (max-width: 480px) {
      font-size: 1.3rem;
    }
  }
  
  p {
    color: var(--dark-gray);
    font-size: 1.1rem;
    
    @media (max-width: 768px) {
      font-size: 1rem;
    }
    
    @media (max-width: 480px) {
      font-size: 0.9rem;
    }
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 1rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const StatCard = styled(Card)`
  text-align: center;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
  }
  
  .stat-icon {
    font-size: 2.5rem;
    margin-bottom: 1rem;
    color: var(--primary-color);
    
    @media (max-width: 768px) {
      font-size: 2rem;
    }
  }
  
  .stat-value {
    font-size: 2rem;
    font-weight: 700;
    color: var(--secondary-color);
    margin-bottom: 0.5rem;
    
    @media (max-width: 768px) {
      font-size: 1.5rem;
    }
  }
  
  .stat-label {
    color: var(--dark-gray);
    font-weight: 500;
    font-size: 1rem;
    
    @media (max-width: 768px) {
      font-size: 0.9rem;
    }
  }
  
  .stat-description {
    font-size: 0.9rem;
    color: var(--medium-gray);
    margin-top: 0.5rem;
    
    @media (max-width: 768px) {
      font-size: 0.8rem;
    }
  }
`;

const ProjectCard = styled(Card)`
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  
  .project-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
    flex-wrap: wrap;
    gap: 1rem;
    
    @media (max-width: 768px) {
      flex-direction: column;
      align-items: flex-start;
    }
  }
  
  .project-title {
    font-size: 1.3rem;
    font-weight: 600;
    color: var(--secondary-color);
    
    @media (max-width: 768px) {
      font-size: 1.1rem;
    }
  }
  
  .project-status {
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-size: 0.9rem;
    font-weight: 600;
    
    @media (max-width: 768px) {
      font-size: 0.8rem;
      padding: 0.4rem 0.8rem;
    }
  }
  
  .project-details {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1.5rem;
    margin-bottom: 1.5rem;
    
    @media (max-width: 768px) {
      grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
      gap: 1rem;
    }
    
    @media (max-width: 480px) {
      grid-template-columns: 1fr;
      gap: 0.75rem;
    }
  }
  
  .detail-item {
    .label {
      font-size: 0.9rem;
      color: var(--dark-gray);
      margin-bottom: 0.25rem;
      font-weight: 500;
      
      @media (max-width: 768px) {
        font-size: 0.8rem;
      }
    }
    
    .value {
      font-weight: 600;
      color: var(--secondary-color);
      font-size: 1rem;
      
      @media (max-width: 768px) {
        font-size: 0.9rem;
      }
    }
  }
  
  .progress-section {
    margin-bottom: 1.5rem;
  }
  
  .progress-bar {
    width: 100%;
    height: 10px;
    background-color: var(--light-gray);
    border-radius: 5px;
    overflow: hidden;
    margin-bottom: 0.5rem;
    
    @media (max-width: 768px) {
      height: 8px;
    }
  }
  
  .progress-fill {
    height: 100%;
    background: linear-gradient(90deg, var(--primary-color), #e07a10);
    transition: width 0.3s ease;
    border-radius: 5px;
  }
  
  .progress-label {
    font-size: 0.9rem;
    color: var(--dark-gray);
    font-weight: 500;
    
    @media (max-width: 768px) {
      font-size: 0.8rem;
    }
  }
`;

const RequestsSection = styled.div`
  margin-bottom: 2rem;
`;

const RequestCard = styled(Card)`
  margin-bottom: 1rem;
  border-left: 4px solid var(--primary-color);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  .request-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 1rem;
    flex-wrap: wrap;
    gap: 1rem;
    
    @media (max-width: 768px) {
      flex-direction: column;
      align-items: flex-start;
    }
  }
  
  .request-id {
    font-weight: 600;
    color: var(--secondary-color);
    font-size: 1.1rem;
    
    @media (max-width: 768px) {
      font-size: 1rem;
    }
  }
  
  .request-amount {
    font-weight: 700;
    color: var(--primary-color);
    font-size: 1.2rem;
    
    @media (max-width: 768px) {
      font-size: 1.1rem;
    }
  }
  
  .request-details {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 1rem;
    margin-bottom: 1rem;
    
    @media (max-width: 768px) {
      grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
      gap: 0.75rem;
    }
    
    @media (max-width: 480px) {
      grid-template-columns: 1fr;
      gap: 0.5rem;
    }
  }
  
  .detail-item {
    .label {
      font-size: 0.85rem;
      color: var(--dark-gray);
      margin-bottom: 0.25rem;
      font-weight: 500;
      
      @media (max-width: 768px) {
        font-size: 0.8rem;
      }
    }
    
    .value {
      font-weight: 500;
      color: var(--secondary-color);
      font-size: 0.9rem;
      
      @media (max-width: 768px) {
        font-size: 0.85rem;
      }
    }
  }
  
  .request-actions {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
    
    @media (max-width: 768px) {
      flex-direction: column;
      gap: 0.75rem;
    }
  }
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: var(--dark-gray);
  
  .empty-icon {
    font-size: 4rem;
    color: var(--light-gray);
    margin-bottom: 1rem;
    
    @media (max-width: 768px) {
      font-size: 3rem;
    }
  }
  
  .empty-title {
    font-size: 1.3rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
    
    @media (max-width: 768px) {
      font-size: 1.1rem;
    }
  }
  
  .empty-description {
    font-size: 1rem;
    line-height: 1.6;
    max-width: 400px;
    margin: 0 auto;
    
    @media (max-width: 768px) {
      font-size: 0.9rem;
      max-width: 300px;
    }
  }
`;

const BudgetSection = styled.div`
  margin-top: 2rem;
`;

const BudgetCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  
  .budget-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
    flex-wrap: wrap;
    gap: 1rem;
    
    @media (max-width: 768px) {
      flex-direction: column;
      align-items: flex-start;
    }
  }
  
  .budget-title {
    font-size: 1.3rem;
    font-weight: 600;
    color: var(--secondary-color);
    
    @media (max-width: 768px) {
      font-size: 1.1rem;
    }
  }
  
  .budget-total {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--primary-color);
    
    @media (max-width: 768px) {
      font-size: 1.3rem;
    }
  }
  
  .budget-breakdown {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1.5rem;
    margin-bottom: 1.5rem;
    
    @media (max-width: 768px) {
      grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
      gap: 1rem;
    }
    
    @media (max-width: 480px) {
      grid-template-columns: 1fr;
      gap: 0.75rem;
    }
  }
  
  .budget-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    background: var(--light-gray);
    border-radius: 8px;
    
    .category {
      font-weight: 600;
      color: var(--secondary-color);
      font-size: 0.95rem;
      
      @media (max-width: 768px) {
        font-size: 0.9rem;
      }
    }
    
    .amount {
      font-weight: 700;
      color: var(--primary-color);
      font-size: 1rem;
      
      @media (max-width: 768px) {
        font-size: 0.9rem;
      }
    }
  }
`;

const AccountingSection = styled.div`
  margin-top: 2rem;
`;

const AccountingCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  
  .accounting-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
    flex-wrap: wrap;
    gap: 1rem;
    
    @media (max-width: 768px) {
      flex-direction: column;
      align-items: flex-start;
    }
  }
  
  .accounting-title {
    font-size: 1.3rem;
    font-weight: 600;
    color: var(--secondary-color);
    
    @media (max-width: 768px) {
      font-size: 1.1rem;
    }
  }
  
  .accounting-stats {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1.5rem;
    margin-bottom: 1.5rem;
    
    @media (max-width: 768px) {
      grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
      gap: 1rem;
    }
    
    @media (max-width: 480px) {
      grid-template-columns: 1fr;
      gap: 0.75rem;
    }
  }
  
  .accounting-stat {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem;
    background: var(--light-gray);
    border-radius: 8px;
    
    .stat-icon {
      font-size: 1.5rem;
      color: var(--primary-color);
      
      @media (max-width: 768px) {
        font-size: 1.3rem;
      }
    }
    
    .stat-info {
      flex: 1;
      
      .stat-label {
        font-size: 0.85rem;
        color: var(--dark-gray);
        margin-bottom: 0.25rem;
        
        @media (max-width: 768px) {
          font-size: 0.8rem;
        }
      }
      
      .stat-value {
        font-weight: 700;
        color: var(--secondary-color);
        font-size: 1.1rem;
        
        @media (max-width: 768px) {
          font-size: 1rem;
        }
      }
    }
    
    .stat-trend {
      display: flex;
      align-items: center;
      gap: 0.25rem;
      font-size: 0.8rem;
      font-weight: 600;
      
      &.positive {
        color: var(--success-color);
      }
      
      &.negative {
        color: var(--danger-color);
      }
    }
  }
`;

const SupervisorDashboard: React.FC = () => {
  const { user } = useAuth();
  const [assignedProject, setAssignedProject] = useState<Project | null>(null);
  const [pendingRequests, setPendingRequests] = useState<FundRequest[]>([]);
  const [stats, setStats] = useState({
    totalRequests: 0,
    pendingApprovals: 0,
    totalAmount: 0,
    spentAmount: 0,
    remainingBudget: 0,
    income: 0,
    balance: 0
  });

  useEffect(() => {
    const fetchAssignedProject = async () => {
      try {
        console.log('Fetching assigned projects for supervisor:', {
          userId: user?.id,
          userName: user?.name,
          userRole: user?.role
        });
        
        // Get projects assigned to the current supervisor
        const assignedProjectsResponse = await apiService.getAssignedProjects();
        const assignedProjects = assignedProjectsResponse.data || [];
        
        console.log('All assigned projects:', assignedProjects);
        
        // Backend now properly filters by role, so we can use the first project
        const supervisorProject = assignedProjects.length > 0 ? assignedProjects[0] : null;
        
        console.log('Found supervisor project:', supervisorProject);
        
        setAssignedProject(supervisorProject);

        // Get fund requests for the assigned project
        if (supervisorProject) {
          const fundRequestsResponse = await apiService.getFundRequests({ 
            projectId: supervisorProject._id || supervisorProject.id 
          });
          const projectRequests = fundRequestsResponse.data || [];
          
          const pending = projectRequests.filter((req: any) => {
            const pendingStep = req.approvalChain?.find((step: any) => step.status === 'Pending');
            return pendingStep?.role === 'Supervisor';
          });
          
          setPendingRequests(pending);
          
          // Calculate stats
          const totalAmount = projectRequests.reduce((sum: number, req: any) => sum + req.amount, 0);
          const spentAmount = projectRequests.filter((req: any) => req.status === 'Paid').reduce((sum: number, req: any) => sum + req.amount, 0);
          const remainingBudget = supervisorProject ? supervisorProject.budget.total - spentAmount : 0;
          const income = supervisorProject?.totalIncome || 0;
          const balance = income - spentAmount;
          
          setStats({
            totalRequests: projectRequests.length,
            pendingApprovals: pending.length,
            totalAmount,
            spentAmount,
            remainingBudget,
            income,
            balance
          });
        }
      } catch (error) {
        console.error("Error fetching assigned project:", error);
      }
    };

    fetchAssignedProject();
  }, [user]);

  const formatCurrency = (amount: number, currency: string = 'TZS') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusVariant = (status: string): 'success' | 'danger' | 'warning' | 'info' | 'primary' => {
    switch (status) {
      case 'Approved': return 'success';
      case 'Rejected': return 'danger';
      case 'Under Review': return 'warning';
      case 'Paid': return 'info';
      case 'Draft': return 'primary';
      default: return 'warning';
    }
  };

  if (!assignedProject) {
    return (
      <DashboardContainer>
        <DashboardHeader>
          <h1>Supervisor Dashboard</h1>
          <p>Welcome back, {user?.name}</p>
        </DashboardHeader>
        
        <NoDataMessage>
          <div className="empty-icon">🏗️</div>
          <div className="empty-title">No Project Assigned</div>
          <div className="empty-description">
            You are not currently assigned to supervise any project. 
            Please contact your project manager or administrator.
          </div>
        </NoDataMessage>
      </DashboardContainer>
    );
  }

  return (
    <DashboardContainer>
      <DashboardHeader>
        <h1>Supervisor Dashboard</h1>
        <p>Welcome back, {user?.name}</p>
      </DashboardHeader>

      <StatsGrid>
        <StatCard>
          <div className="stat-icon">
            <IconWrapper icon={FaChartLine} />
          </div>
          <div className="stat-value">{formatCurrency(stats.remainingBudget)}</div>
          <div className="stat-label">Remaining Budget</div>
          <div className="stat-description">Available funds</div>
        </StatCard>

        <StatCard>
          <div className="stat-icon">
            <IconWrapper icon={FaMoneyBillWave} />
          </div>
          <div className="stat-value">{stats.totalRequests}</div>
          <div className="stat-label">Total Requests</div>
          <div className="stat-description">Fund requests for project</div>
        </StatCard>

        <StatCard>
          <div className="stat-icon">
            <IconWrapper icon={FaClock} />
          </div>
          <div className="stat-value">{stats.pendingApprovals}</div>
          <div className="stat-label">Pending Approvals</div>
          <div className="stat-description">Awaiting your review</div>
        </StatCard>

        <StatCard>
          <div className="stat-icon">
            <IconWrapper icon={FaCalculator} />
          </div>
          <div className="stat-value">{formatCurrency(stats.spentAmount)}</div>
          <div className="stat-label">Spent Amount</div>
          <div className="stat-description">Total amount spent</div>
        </StatCard>
      </StatsGrid>

      <ProjectCard>
        <div className="project-header">
          <div className="project-title">{assignedProject.name}</div>
          <Badge variant={getStatusVariant(assignedProject.status)} className="project-status">
            {assignedProject.status}
          </Badge>
        </div>

        <div className="project-details">
          <div className="detail-item">
            <div className="label">Project ID</div>
            <div className="value">{assignedProject._id || assignedProject.id}</div>
          </div>
          <div className="detail-item">
            <div className="label">Location</div>
            <div className="value">{assignedProject.location}</div>
          </div>
          <div className="detail-item">
            <div className="label">Start Date</div>
            <div className="value">{formatDate(assignedProject.startDate)}</div>
          </div>
          <div className="detail-item">
            <div className="label">End Date</div>
            <div className="value">{formatDate(assignedProject.endDate || new Date())}</div>
          </div>
          <div className="detail-item">
            <div className="label">Budget</div>
            <div className="value">{formatCurrency(assignedProject.budget.total)}</div>
          </div>
          <div className="detail-item">
            <div className="label">Progress</div>
            <div className="value">{assignedProject.progress || 0}%</div>
          </div>
        </div>

        <div className="progress-section">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${assignedProject.progress || 0}%` }}
            />
          </div>
          <div className="progress-label">
            Project Progress
          </div>
        </div>

        <CardFooter>
          <Button as={Link} to="/supervisor/project">
            <IconWrapper icon={FaEye} />
            View Project Details
          </Button>
        </CardFooter>
      </ProjectCard>

      <BudgetSection>
        <BudgetCard>
          <div className="budget-header">
            <div className="budget-title">Project Budget Breakdown</div>
            <div className="budget-total">{formatCurrency(assignedProject.budget.total)}</div>
          </div>
          
          <div className="budget-breakdown">
            <div className="budget-item">
              <div className="category">Materials</div>
              <div className="amount">{formatCurrency(assignedProject.budget.materials)}</div>
            </div>
            <div className="budget-item">
              <div className="category">Labor</div>
              <div className="amount">{formatCurrency(assignedProject.budget.labor)}</div>
            </div>
            <div className="budget-item">
              <div className="category">Equipment</div>
              <div className="amount">{formatCurrency(assignedProject.budget.equipment)}</div>
            </div>
            <div className="budget-item">
              <div className="category">Sub-contracts</div>
              <div className="amount">{formatCurrency(assignedProject.budget.subContracts)}</div>
            </div>
            <div className="budget-item">
              <div className="category">Others</div>
              <div className="amount">{formatCurrency(assignedProject.budget.others)}</div>
            </div>
          </div>
        </BudgetCard>
      </BudgetSection>

      <AccountingSection>
        <AccountingCard>
          <div className="accounting-header">
            <div className="accounting-title">Project Financial Overview</div>
          </div>
          
          <div className="accounting-stats">
            <div className="accounting-stat">
              <div className="stat-icon">
                <IconWrapper icon={FaMoneyBillWave} />
              </div>
              <div className="stat-info">
                <div className="stat-label">Total Income</div>
                <div className="stat-value">{formatCurrency(stats.income)}</div>
              </div>
              <div className="stat-trend positive">
                <IconWrapper icon={FaArrowUp} />
                +12%
              </div>
            </div>
            
            <div className="accounting-stat">
              <div className="stat-icon">
                <IconWrapper icon={FaFileInvoiceDollar} />
              </div>
              <div className="stat-info">
                <div className="stat-label">Total Spent</div>
                <div className="stat-value">{formatCurrency(stats.spentAmount)}</div>
              </div>
              <div className="stat-trend negative">
                <IconWrapper icon={FaArrowDown} />
                -8%
              </div>
            </div>
            
            <div className="accounting-stat">
              <div className="stat-icon">
                <IconWrapper icon={FaCalculator} />
              </div>
              <div className="stat-info">
                <div className="stat-label">Current Balance</div>
                <div className="stat-value">{formatCurrency(stats.balance)}</div>
              </div>
              <div className="stat-trend positive">
                <IconWrapper icon={FaArrowUp} />
                +4%
              </div>
            </div>
            
            <div className="accounting-stat">
              <div className="stat-icon">
                <IconWrapper icon={FaChartLine} />
              </div>
              <div className="stat-info">
                <div className="stat-label">Remaining Budget</div>
                <div className="stat-value">{formatCurrency(stats.remainingBudget)}</div>
              </div>
              <div className="stat-trend positive">
                <IconWrapper icon={FaArrowUp} />
                +15%
              </div>
            </div>
          </div>
        </AccountingCard>
      </AccountingSection>

      <RequestsSection>
        <CardHeader>
          <h3>Pending Fund Request Approvals</h3>
          <Button as={Link} to="/supervisor/funds/new">
            <IconWrapper icon={FaPlus} />
            New Request
          </Button>
        </CardHeader>

        {pendingRequests.length > 0 ? (
          pendingRequests.map((request) => (
            <RequestCard key={request.id}>
              <div className="request-header">
                <div>
                  <div className="request-id">{request.id}</div>
                  <div style={{ fontSize: '0.9rem', color: 'var(--dark-gray)' }}>
                    {request.projectName}
                  </div>
                </div>
                <div className="request-amount">
                  {formatCurrency(request.amount, request.currency)}
                </div>
              </div>

              <div className="request-details">
                <div className="detail-item">
                  <div className="label">Timeline</div>
                  <div className="value">{request.timeline}</div>
                </div>
                <div className="detail-item">
                  <div className="label">Created</div>
                  <div className="value">{formatDate(request.createdAt)}</div>
                </div>
                <div className="detail-item">
                  <div className="label">Status</div>
                  <div className="value">
                    <Badge variant={getStatusVariant(request.status)}>
                      {request.status}
                    </Badge>
                  </div>
                </div>
              </div>

              <div className="request-actions">
                <Button as={Link} to={`/supervisor/funds/${request.id}`}>
                  <IconWrapper icon={FaEye} />
                  View Details
                </Button>
                <Button style={{ backgroundColor: 'var(--success-color)' }}>
                  <IconWrapper icon={FaCheckCircle} />
                  Approve
                </Button>
                <Button style={{ backgroundColor: 'var(--danger-color)' }}>
                  <IconWrapper icon={FaExclamationTriangle} />
                  Reject
                </Button>
              </div>
            </RequestCard>
          ))
        ) : (
          <NoDataMessage>
            <div className="empty-icon">📋</div>
            <div className="empty-title">No Pending Approvals</div>
            <div className="empty-description">
              All fund requests have been processed. You can create new requests when needed.
            </div>
          </NoDataMessage>
        )}
      </RequestsSection>
    </DashboardContainer>
  );
};

export default SupervisorDashboard; 